'use client'

import { X } from 'lucide-react'

import { Button, List } from '~/components/component-library'
import { useAutoEarnToggle } from '~/components/forms/earn/useAutoEarnToggle'
import { StepDone } from '~/components/post-onboarding-checklist/StepDone'
import { WorkspaceAutoEarnStepAction } from '~/components/post-onboarding-checklist/WorkspaceAutoEarnStepAction'
import {
  getTeammateStepMeta,
  WAITING_FOR_INVITATION,
} from '~/components/post-onboarding-checklist/teammateStep'
import { isChecklistStepComplete } from '~/components/post-onboarding-checklist/utils'
import { InviteMemberDialogContent } from '~/components/settings/InviteMemberDialogContent'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { usePendingAutoEarnTransaction } from '~/hooks/smart-accounts/usePendingAutoEarnTransaction'
import { usePostOnboardingChecklistUpdates } from '~/hooks/usePostOnboardingChecklist'
import { useServerRequests } from '~/hooks/useRequests'
import {
  OrgSmartAccount,
  PostOnboardingChecklist,
  PostOnboardingChecklistStepKey,
} from '~/types'

// An enable transaction that is created but unsigned sits in the feed right
// below this bar with its own Review button, so the step says what it is
// waiting for rather than repeating that action.
const AutoEarnStepAction = ({
  treasuryAccount,
}: {
  treasuryAccount: OrgSmartAccount
}) => {
  const { handleToggle, isDisabled, disabledReason, isProcessing } =
    useAutoEarnToggle(treasuryAccount)
  const pendingTransaction = usePendingAutoEarnTransaction(treasuryAccount.id)

  if (pendingTransaction) {
    return <StepMeta>Waiting for approval</StepMeta>
  }

  return (
    <Button
      variant="secondary"
      size="small"
      isLoading={isProcessing}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
      onClick={() => {
        handleToggle(true)
      }}
    >
      Activate
    </Button>
  )
}

// Slots align to the row top. A small button's height on each lines them up.
const StepLabel = ({ children }: { children: string }) => (
  <span className="flex min-h-8 items-center">{children}</span>
)

const StepMeta = ({ children }: { children: string }) => (
  <span className="flex h-8 items-center text-sm">{children}</span>
)

const getTeammateStepLabel = ({
  isTeammateAdded,
  hasPendingInvitation,
}: {
  isTeammateAdded: boolean
  hasPendingInvitation: boolean
}) => {
  if (isTeammateAdded) return 'Teammate added'
  if (hasPendingInvitation) return 'Invitation sent'
  return 'Add a teammate'
}

// The checklist in the dashboard's right sidebar once the org is funded.
// Dismissing is the single user control and is per-user — it never changes
// what other members see. Once every step is done it shows a success message
// in place of the list.
export const PostOnboardingChecklistPanel = ({
  checklist,
  treasuryAccount,
}: {
  checklist: PostOnboardingChecklist
  treasuryAccount: OrgSmartAccount
}) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { org, featureFlags } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { setPostOnboardingChecklist } = usePostOnboardingChecklistUpdates()

  const isFunded = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.Fund,
  )
  const isTeammateAdded = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AddTeammate,
  )
  const isAutoEarnOn = isChecklistStepComplete(
    checklist,
    PostOnboardingChecklistStepKey.AutoEarn,
  )
  const hasPendingInvitation = !isTeammateAdded && !!org?.invitations.length

  const openInviteDialog = () => {
    setGlobalModalState({
      isOpen: true,
      content: <InviteMemberDialogContent closeDialog={closeGlobalModal} />,
    })
  }

  const handleHide = async () => {
    const result = await serverPostRequest({
      path: '/api/org/post_onboarding_checklist/hide',
      params: { orgId: checklist.orgId },
      errorMessage: 'Failed to hide the checklist',
    })
    // The endpoint sends no body. Hiding is per user, thus the flag is the
    // only field that changes.
    if (result) setPostOnboardingChecklist({ ...checklist, hidden: true })
  }

  return (
    <List
      label="Getting started"
      secondaryDisplay={
        // The header aligns its label and this slot at the top, so the
        // button's own padding would drop the X below the label's text.
        <div className="-my-1.5">
          <Button
            aria-label="Dismiss checklist"
            variant="tertiary"
            size="small"
            isLoading={isProcessing}
            prefix={<X height={12} width={12} />}
            onClick={handleHide}
          />
        </div>
      }
      items={[
        {
          key: 'fund',
          content: <StepLabel>Fund your treasury</StepLabel>,
          meta: isFunded ? <StepDone /> : undefined,
        },
        {
          key: 'add-teammate',
          content: (
            <StepLabel>
              {getTeammateStepLabel({ isTeammateAdded, hasPendingInvitation })}
            </StepLabel>
          ),
          meta: hasPendingInvitation ? (
            <StepMeta>{WAITING_FOR_INVITATION}</StepMeta>
          ) : (
            getTeammateStepMeta({
              isTeammateAdded,
              hasPendingInvitation,
              isOwner,
              openInviteDialog,
            })
          ),
        },
        {
          key: 'auto-earn',
          content: <StepLabel>Earn on idle cash</StepLabel>,
          meta: isAutoEarnOn ? (
            <StepDone />
          ) : featureFlags?.['workspace-auto-earn'] ? (
            <WorkspaceAutoEarnStepAction
              pendingAction={() => <StepMeta>Waiting for approval</StepMeta>}
            />
          ) : (
            <AutoEarnStepAction treasuryAccount={treasuryAccount} />
          ),
        },
      ]}
    />
  )
}
